import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articleCards } from '@/mocks/articles';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setCategoryOpen(false);
      }
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) {
        setGuideOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const linkClass = (isScrolled: boolean) =>
    `flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
      isScrolled
        ? 'text-foreground-700 hover:bg-secondary-100 hover:text-foreground-900'
        : 'text-white/85 hover:text-white hover:bg-white/10'
    }`;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="flex items-center gap-2 group"
            >
              <span className={`font-heading text-xl md:text-2xl font-semibold transition-colors duration-300 ${
                scrolled ? 'text-foreground-950' : 'text-white'
              }`}>
                Sugargoo Spreadsheet
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-1 ml-4">
              <Link to="/" className={linkClass(scrolled)}>
                <i className="ri-home-line"></i>
                Home
              </Link>

              <div className="relative" ref={guideRef}>
                <button
                  onClick={() => { setGuideOpen(!guideOpen); setCategoryOpen(false); }}
                  className={linkClass(scrolled)}
                >
                  <i className="ri-guide-line"></i>
                  Shopping Guides
                  <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${guideOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {guideOpen && (
                  <div className="absolute top-full mt-2 left-0 w-72 bg-background-50 rounded-xl border border-background-200/70 shadow-lg overflow-hidden z-50 animate-[fadeIn_0.15s_ease-out]">
                    <div className="max-h-80 overflow-y-auto py-2">
                      {articleCards.map((card) => (
                        <Link
                          key={card.slug}
                          to={card.slug}
                          onClick={() => setGuideOpen(false)}
                          className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-100 hover:text-accent-800 transition-colors duration-150"
                        >
                          <span className="line-clamp-1 font-medium">{card.title}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => { setCategoryOpen(!categoryOpen); setGuideOpen(false); }}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  scrolled
                    ? 'bg-secondary-100 text-foreground-800 hover:bg-secondary-200'
                    : 'bg-white/15 text-white hover:bg-white/25 backdrop-blur-sm'
                }`}
              >
                <i className="ri-grid-line"></i>
                Categories
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${categoryOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {categoryOpen && (
                <div className="absolute top-full mt-2 right-0 w-64 bg-background-50 rounded-xl border border-background-200/70 shadow-lg overflow-hidden z-50 animate-[fadeIn_0.15s_ease-out]">
                  <div className="max-h-80 overflow-y-auto py-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={() => setCategoryOpen(false)}
                        className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-100 hover:text-accent-800 transition-colors duration-150 whitespace-nowrap"
                      >
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                scrolled
                  ? 'bg-primary-500 text-white hover:bg-primary-600'
                  : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
              }`}
            >
              Sugargoo Spreadsheet
              <i className="ri-external-link-line text-xs"></i>
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all duration-200 cursor-pointer ${
                scrolled
                  ? 'bg-accent-500 text-white hover:bg-accent-600'
                  : 'bg-accent-500 text-white hover:bg-accent-600'
              }`}
            >
              Buy It Now
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden flex items-center justify-center w-10 h-10 rounded-lg transition-colors duration-200 cursor-pointer ${
              scrolled ? 'text-foreground-800 hover:bg-secondary-100' : 'text-white hover:bg-white/10'
            }`}
            aria-label="Toggle navigation menu"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 animate-[fadeIn_0.2s_ease-out] max-h-[calc(100vh-64px)] overflow-y-auto overscroll-contain">
          <div className="px-4 py-3 space-y-2">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-secondary-100 rounded-lg transition-colors duration-150"
            >
              <i className="ri-home-line text-accent-600"></i>
              Home
            </Link>

            <div className="border-t border-background-200/60 pt-2 mt-1">
              <p className="text-xs font-medium text-foreground-500 uppercase tracking-wide px-3 py-2 flex items-center gap-2">
                <i className="ri-grid-line text-accent-500"></i>
                Product Categories
              </p>
              <div className="grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-3 py-2.5 text-xs text-foreground-700 hover:bg-accent-100 hover:text-accent-800 rounded-lg transition-colors duration-150 whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>

            <div className="border-t border-background-200/60 pt-2 mt-1">
              <p className="text-xs font-medium text-foreground-500 uppercase tracking-wide px-3 py-2 flex items-center gap-2">
                <i className="ri-guide-line text-primary-500"></i>
                Shopping Guides
              </p>
              {articleCards.map((card) => (
                <Link
                  key={card.slug}
                  to={card.slug}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-2.5 text-sm text-foreground-700 hover:bg-secondary-100 hover:text-foreground-900 rounded-lg transition-colors duration-150"
                >
                  <span className="line-clamp-1">{card.title}</span>
                </Link>
              ))}
            </div>

            <div className="border-t border-background-200/70 pt-3 mt-2 space-y-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-2 px-3 py-3 text-sm font-semibold bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors duration-150"
              >
                <i className="ri-external-link-line"></i>
                Sugargoo Spreadsheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-2 px-3 py-3 text-sm font-semibold bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors duration-150"
              >
                <i className="ri-shopping-bag-line"></i>
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}